/**
 * POS_PLANS_SSOT
 * Orden: POS-PLANS-SSOT-001B-CLOSURE-001
 */

'use strict';

const PLAN_IDS = ['basic', 'intermediate', 'premium'];
const PLAN_TIERS = PLAN_IDS;
const PLAN_ID_START = 'basic';
const PLAN_ID_PRO = 'intermediate';
const PLAN_ID_EMPRESA = 'premium';
const PLAN_COMMERCIAL_NAMES = { basic: 'START', intermediate: 'PRO', premium: 'EMPRESA' };
const PLAN_LABELS_ES = { trial: 'Cortesía inicial', demo: 'Cortesía inicial', basic: PLAN_COMMERCIAL_NAMES.basic, intermediate: PLAN_COMMERCIAL_NAMES.intermediate, premium: PLAN_COMMERCIAL_NAMES.premium };
const PLAN_LEGACY_LABELS_ES = { basic: 'Básica', intermediate: 'Intermedia', premium: 'Premium' };
const PLAN_PRICES_ARS = { basic: 80000, intermediate: 150000, premium: 280000 };
const OFFICIAL_PLAN_PRICES_ARS = PLAN_PRICES_ARS;
const LEGACY_PLAN_PRICES_ARS = { basic: 80000, intermediate: 120000, premium: 180000 };
const TRIAL_DAYS = 30;
const TRIAL_POLICY = {
  days: TRIAL_DAYS,
  accessRestricted: false,
  fullAccessGranted: true,
  blocked: false,
  phaseActive: 'trial_active',
  phaseExpired: 'trial_expired',
  phasePendingPayment: 'restricted_pending_payment',
  demo: false
};
function getTrialEndsAtDate(fromDate = new Date()) {
  const d = new Date(fromDate.getTime());
  d.setDate(d.getDate() + TRIAL_DAYS);
  return d;
}
const DEMO_HOURS = 48;
const BILLING_CYCLE_DAYS = 30;
const PLAN_MODULE_KEYS = [
  'productos',
  'categorias',
  'clientes',
  'proveedores',
  'compras',
  'ventas',
  'punto_venta',
  'stock',
  'listas_precios',
  'combos',
  'transferencias',
  'reportes',
  'promociones',
  'caja',
  'gastos',
  'devoluciones',
  'auditoria',
  'vehiculos',
  'produccion',
  'recetas',
  'materias_primas',
  'configuracion',
  'sucursales',
  'usuarios'
];
const PLAN_COMMERCIAL_META = {
  basic: { commercialName: PLAN_COMMERCIAL_NAMES.basic, includedUsers: 2, includedBranches: 1, extraUserArs: 12000, extraBranchArs: 25000 },
  intermediate: { commercialName: PLAN_COMMERCIAL_NAMES.intermediate, includedUsers: 6, includedBranches: 3, extraUserArs: 10000, extraBranchArs: 22000 },
  premium: { commercialName: PLAN_COMMERCIAL_NAMES.premium, includedUsers: 15, includedBranches: 10, extraUserArs: 9000, extraBranchArs: 20000 }
};
function presetPremiumModules() {
  const m = {};
  for (const k of PLAN_MODULE_KEYS) m[k] = true;
  return m;
}
function presetIntermediateModules() {
  return {
    productos: true,
    categorias: true,
    clientes: true,
    proveedores: true,
    compras: true,
    ventas: true,
    punto_venta: true,
    stock: true,
    listas_precios: true,
    combos: true,
    transferencias: true,
    reportes: true,
    promociones: false,
    caja: true,
    gastos: true,
    devoluciones: true,
    auditoria: true,
    vehiculos: false,
    produccion: false,
    recetas: false,
    materias_primas: false,
    configuracion: true,
    sucursales: true,
    usuarios: true,
  };
}
function presetBasicModules() {
  return {
    productos: true,
    categorias: true,
    clientes: true,
    proveedores: true,
    compras: true,
    ventas: true,
    punto_venta: true,
    stock: true,
    listas_precios: false,
    combos: false,
    transferencias: false,
    reportes: true,
    promociones: false,
    caja: true,
    gastos: true,
    devoluciones: true,
    auditoria: false,
    vehiculos: false,
    produccion: false,
    recetas: false,
    materias_primas: false,
    configuracion: true,
    sucursales: true,
    usuarios: true,
  };
}
function stripDiacritics(input) {
  return String(input || '').normalize('NFD').replace(/[̀-ͯ]/g, '');
}
function normalizePlan(raw) {
  const s = stripDiacritics(String(raw || 'basic').toLowerCase().trim());
  if (s === 'start' || s === 'basica' || s === 'basico') return 'basic';
  if (s === 'pro' || s === 'intermedia' || s === 'intermedio') return 'intermediate';
  if (s === 'empresa' || s === 'enterprise') return 'premium';
  if (PLAN_TIERS.includes(s)) return s;
  return 'basic';
}
function normalizeLicensePlan(raw) {
  const s = stripDiacritics(String(raw || 'basic').toLowerCase().trim());
  if (s === 'demo' || s === 'trial') return 'trial';
  return normalizePlan(s);
}
function getCommercialPlanName(rawPlan) {
  const tier = normalizePlan(rawPlan);
  return PLAN_COMMERCIAL_NAMES[tier] || tier.toUpperCase();
}
function defaultPlanPrices() {
  return { basic: PLAN_PRICES_ARS.basic, intermediate: PLAN_PRICES_ARS.intermediate, premium: PLAN_PRICES_ARS.premium };
}
function getPlanPriceARS(rawPlan) {
  return PLAN_PRICES_ARS[normalizePlan(rawPlan)];
}
function isLegacySilentPlanPrice(tier, value) {
  const t = normalizePlan(tier);
  const n = Number(value);
  if (Number.isNaN(n)) return false;
  if (t === "intermediate" && n === LEGACY_PLAN_PRICES_ARS.intermediate) return true;
  if (t === "premium" && n === LEGACY_PLAN_PRICES_ARS.premium) return true;
  return false;
}
function resolvePlanPrices(firestorePlanPrices, legacyMonthlyBasic) {
  const prices = defaultPlanPrices();
  const pp = firestorePlanPrices && typeof firestorePlanPrices === 'object' ? firestorePlanPrices : {};
  for (const k of PLAN_TIERS) {
    if (pp[k] == null || Number.isNaN(Number(pp[k]))) continue;
    const n = Number(pp[k]);
    if (n < 0) continue;
    if (isLegacySilentPlanPrice(k, n)) continue;
    prices[k] = n;
  }
  const legacy = Number(legacyMonthlyBasic == null ? 0 : legacyMonthlyBasic);
  if (prices.basic <= 0 && !Number.isNaN(legacy) && legacy > 0) prices.basic = legacy;
  return prices;
}
function getModulePresetForPlan(rawPlan) {
  const p = normalizePlan(rawPlan);
  if (p === 'premium') return presetPremiumModules();
  if (p === 'intermediate') return presetIntermediateModules();
  return presetBasicModules();
}
function getPlanCommercialMeta(rawPlan) {
  const tier = normalizePlan(rawPlan);
  return Object.assign({}, PLAN_COMMERCIAL_META[tier]);
}
function getPlanCommercialMatrix() {
  return {
    START: Object.assign({ tier: 'basic', modules: presetBasicModules() }, PLAN_COMMERCIAL_META.basic),
    PRO: Object.assign({ tier: 'intermediate', modules: presetIntermediateModules() }, PLAN_COMMERCIAL_META.intermediate),
    EMPRESA: Object.assign({ tier: 'premium', modules: presetPremiumModules() }, PLAN_COMMERCIAL_META.premium)
  };
}
module.exports = {
  PLAN_IDS, PLAN_TIERS, PLAN_ID_START, PLAN_ID_PRO, PLAN_ID_EMPRESA,
  PLAN_COMMERCIAL_NAMES, PLAN_LABELS_ES, PLAN_LEGACY_LABELS_ES,
  PLAN_PRICES_ARS, OFFICIAL_PLAN_PRICES_ARS, LEGACY_PLAN_PRICES_ARS,
  TRIAL_DAYS, TRIAL_POLICY, getTrialEndsAtDate, DEMO_HOURS, BILLING_CYCLE_DAYS, PLAN_MODULE_KEYS, PLAN_COMMERCIAL_META,
  presetBasicModules, presetIntermediateModules, presetPremiumModules,
  normalizePlan, normalizeLicensePlan, getCommercialPlanName,
  defaultPlanPrices, getPlanPriceARS, isLegacySilentPlanPrice, resolvePlanPrices,
  getModulePresetForPlan, getPlanCommercialMeta, getPlanCommercialMatrix
};
